import { useEffect } from 'react';

import { useAppDispatch, useAppSelector } from '../../hooks/store';
import {
  isModalVisibleSelector,
  modalSelector,
} from '../../state/selectors/modal';
import { ModalType, hideModal } from '../../state/slices/modal';
import { BulkCompleteRouteModal } from './BulkCompleteRouteModal';
import { BulkMarkPickedUpModal } from './BulkMarkPickedUpModal';
import { CustomerPickupRecentDeliveriesModal } from './CustomerPickupRecentDeliveriesModal';
import { CustomerPickupToggleModal } from './CustomerPickupToggleModal';
import { MarkPickedUpModal } from './MarkPickedUpModal';
import { EscalateStopsToProviderModal } from './EscalateStopsToProviderModal';
import { MarkRouteForProviderModal } from './MarkRouteForProviderModal';
import { MoveStopToCustomerPickupModal } from './MoveStopToCustomerPickupModal';
import { MoveStopToOdekoDeliveriesModal } from './MoveStopToOdekoDeliveriesModal';
import BulkEditLocationsModal from './BulkEditLocationsModal';
import ConfirmationModal from './ConfirmationModal';
import CreateRouteButton from './CreateRouteButton';
import FullscreenModal from './FullscreenModal';
import MetrobiIneligibleModal from './MetrobiIneligibleModal';
import { ReopenRouteModal } from './ReopenRouteModal';
import { UnmarkRouteProviderModal } from './UnmarkRouteProviderModal';
import { backdrop, container } from './styles';
import UpdateBulkItemsModal from './UpdateBulkItemsModal';
import UpdateTruckCountButton from './UpdateTruckCountButton';

export default function CustomModal(): JSX.Element | null {
  const { type, variables } = useAppSelector(modalSelector);
  const dispatch = useAppDispatch();
  const isModalVisible = useAppSelector(isModalVisibleSelector);

  const handleOnClickCancel = (): void => {
    dispatch(hideModal());
  };

  useEffect(() => {
    const close = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && type === ModalType.Fullscreen) {
        handleOnClickCancel();
      }
    };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  });

  if (!isModalVisible) return null;

  return (
    <div className="custom-modal" sx={container}>
      <div
        sx={backdrop}
        onClick={
          type === ModalType.Fullscreen ? handleOnClickCancel : (): void => {}
        }
      />
      {type === ModalType.CreateRoute && (
        <ConfirmationModal>
          <CreateRouteButton />
        </ConfirmationModal>
      )}
      {type === ModalType.UpdateTruckCount && (
        <ConfirmationModal>
          <UpdateTruckCountButton />
        </ConfirmationModal>
      )}
      {type === ModalType.BulkCompleteRoute && (
        <ConfirmationModal>
          <BulkCompleteRouteModal />
        </ConfirmationModal>
      )}
      {type === ModalType.ReopenRoute && (
        <ConfirmationModal>
          <ReopenRouteModal />
        </ConfirmationModal>
      )}
      {type === ModalType.UnmarkRouteProvider && <UnmarkRouteProviderModal />}
      {type === ModalType.MarkRouteForProvider && <MarkRouteForProviderModal />}
      {type === ModalType.EscalateStopsToProvider && (
        <EscalateStopsToProviderModal />
      )}
      {type === ModalType.MarkPickedUp && <MarkPickedUpModal />}
      {type === ModalType.BulkMarkPickedUp && <BulkMarkPickedUpModal />}
      {type === ModalType.MoveStopToOdekoDeliveries && (
        <MoveStopToOdekoDeliveriesModal />
      )}
      {type === ModalType.MoveStopToCustomerPickup && (
        <MoveStopToCustomerPickupModal />
      )}
      {type === ModalType.CustomerPickupToggle && <CustomerPickupToggleModal />}
      {type === ModalType.CustomerPickupRecentDeliveries && (
        // Keyed on the request that produced it: the dialog snapshots its
        // offender list at mount, so a second rejection arriving through the
        // same modal type would otherwise re-render the first one's list and
        // skipping would never converge.
        <CustomerPickupRecentDeliveriesModal key={variables?.requestNonce} />
      )}
      {type === ModalType.BulkEditLocationsModal && <BulkEditLocationsModal />}
      {type === ModalType.UpdateBulkItems && <UpdateBulkItemsModal />}
      {type === ModalType.Fullscreen && <FullscreenModal />}
      {type === ModalType.MetrobiIneligible && <MetrobiIneligibleModal />}
    </div>
  );
}
